'use client';

import { ErrorCode } from '@lib/errors/error-code';
import { useClientTranslation } from '@lib/i18n/client';
import { captureError } from '@lib/sentry/capture-error-code';
import {
  extractPathFromLocationUri,
  findTitleByPathPrefix,
} from '@modules/mxp-dialog/helpers';
import { useDialogTelemetry } from '@modules/prism-client/lib/telemetry';
import { Trans } from '@tuic/i18n';
import { TextButton } from '@tuic/web-compositions/core/TextButton';
import { AnimatedShipIndicator } from '@tuic/web-compositions/shared/AnimatedShipIndicator';
import { DialogPersistent } from '@tuic/web-compositions/shared/DialogPersistent';
import { useToasts } from '@tuic/web-compositions/shared/Toaster';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { Mxp365DebugFooter } from '../Mxp365DebugFooter';
import { Mxp365Error } from '../Mxp365Error';

import { createMxp365V2OpenEvent } from './events-v2';
import { Mxp365IframeV2, type MxpIframeV2Handle } from './Mxp365IframeV2';

import type { MxpDialogTitleCollection } from '../mxp365.types';
import type { MxpModuleIdV2, MxpV2ErrorCode } from './mxp365-v2.types';
import type PrismAPI from '@modules/prism-client/types/prism-types';

// Contract §7.2: these errors indicate a broken SignalR/WebSocket connection —
// reload the iframe to re-establish rather than showing an error screen.
const RELOAD_WORTHY_ERRORS = new Set<MxpV2ErrorCode>([
  'NAVIGATION_TIMEOUT',
  'WEBSOCKET_DISCONNECTED',
  'CLIENT_TIMEOUT',
]);
import { MxpNavigationError } from './mxp-navigation-error';

type Props = {
  titles?: MxpDialogTitleCollection;
};

export type Mxp365DialogV2Ref = {
  initiateNavigation: (args: {
    module: MxpModuleIdV2;
    parameter?: string | null;
    title: string;
    token: string;
    queryParams?: Record<string, string>;
    telemetry?: PrismAPI.TelemetryInstruction[];
  }) => void;
};

const MXP_DIALOG_NAV_ATTR = 'data-mxp-dialog-nav';

const mapV2ErrorCode = (code: MxpV2ErrorCode): ErrorCode => {
  switch (code) {
    case 'MODULE_NOT_FOUND':
      return ErrorCode.Mxp365NotFound;
    case 'NAVIGATION_TIMEOUT':
    case 'CLIENT_TIMEOUT':
    case 'WEBSOCKET_DISCONNECTED':
    case 'NAVIGATION_FAILED':
      return ErrorCode.Mxp365NavigationFailed;
    default:
      return ErrorCode.Mxp365GeneralError;
  }
};

export const Mxp365DialogV2 = forwardRef<Mxp365DialogV2Ref, Props>(
  function Mxp365DialogV2({ titles }, ref) {
    const { t } = useClientTranslation();
    const { push } = useToasts();
    const iframeRef = useRef<MxpIframeV2Handle>(null);
    const notOnProd = process.env.NODE_ENV !== 'production';

    const [isOpen, setIsOpen] = useState(false);
    const [currentModule, setCurrentModule] = useState<MxpModuleIdV2 | null>(
      null,
    );
    const [currentParameter, setCurrentParameter] = useState<string | null>(
      null,
    );
    const [currentTitle, setCurrentTitle] = useState('MXP365');
    const [currentToken, setCurrentToken] = useState<string>('');
    const [currentQueryParams, setCurrentQueryParams] = useState<
      Record<string, string> | undefined
    >();
    const [telemetry, setTelemetry] =
      useState<PrismAPI.TelemetryInstruction[]>();
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState<ErrorCode | null>(null);
    const [showBackButton, setShowBackButton] = useState(false);
    const [dynamicTitle, setDynamicTitle] = useState<string | undefined>();
    const [hasBeenOpened, setHasBeenOpened] = useState(false);
    // Incrementing this key forces Mxp365IframeV2 to remount (re-establishes
    // the SignalR connection) after a NAVIGATION_TIMEOUT / WEBSOCKET_DISCONNECTED
    // / CLIENT_TIMEOUT error. The new frozen source picks up currentModule so the
    // iframe reloads to the right page.
    const [iframeKey, setIframeKey] = useState(0);
    // Navigation generation — every initiateNavigation increments it so that
    // a stale promise resolution from a superseded request cannot flip the
    // pending/error state of the new one.
    const navGenerationRef = useRef(0);

    useDialogTelemetry(telemetry, isOpen);

    const navigateInIframe = useCallback(
      async (module: MxpModuleIdV2, parameter: string | null) => {
        const handle = iframeRef.current;
        if (!handle) return;
        const generation = ++navGenerationRef.current;
        try {
          await handle.navigate(module, parameter);
          if (navGenerationRef.current !== generation) return;
          setIsPending(false);
        } catch (e) {
          if (navGenerationRef.current !== generation) return;
          if (e instanceof MxpNavigationError) {
            if (RELOAD_WORTHY_ERRORS.has(e.code)) {
              setIframeKey(k => k + 1);
              // Keep isPending=true; the remounted iframe's direct-load will resolve it.
              return;
            }
            const errorCode = mapV2ErrorCode(e.code);
            setError(errorCode);
            setIsPending(false);
          } else {
            captureError(
              e instanceof Error
                ? e
                : new Error(`Unknown MXP V2 navigation failure: ${String(e)}`),
            );
            setError(ErrorCode.Mxp365GeneralError);
            setIsPending(false);
          }
        }
      },
      [],
    );

    const initiateNavigation = useCallback<
      Mxp365DialogV2Ref['initiateNavigation']
    >(
      ({
        module,
        parameter = null,
        title,
        token,
        queryParams,
        telemetry: nextTelemetry,
      }) => {
        setIsOpen(true);
        setCurrentModule(module);
        setCurrentParameter(parameter ?? null);
        setDynamicTitle(undefined);
        setCurrentTitle(title);
        setCurrentToken(token);
        setCurrentQueryParams(queryParams);
        setTelemetry(nextTelemetry);
        setIsPending(true);
        setError(null);
        setHasBeenOpened(true);

        // If the iframe is already mounted, drive a programmatic navigation.
        // First-mount goes through `initialModule` on the iframe, so the
        // direct-load `moduleReadyV2` will resolve the spinner instead.
        if (iframeRef.current && hasBeenOpened) {
          void navigateInIframe(module, parameter ?? null);
        }
      },
      [currentModule, currentParameter, hasBeenOpened, navigateInIframe],
    );

    useImperativeHandle(ref, () => ({ initiateNavigation }), [
      initiateNavigation,
    ]);

    const handleNavigation = useCallback(
      ({ allowedNavigateBack }: { allowedNavigateBack: boolean }) => {
        setShowBackButton(allowedNavigateBack);
      },
      [],
    );

    const handleBack = useCallback(() => {
      iframeRef.current?.postMessage({ action: 'navigateBack' });
    }, []);

    const handleShare = useCallback(
      (args: { url: string; title?: string; text?: string }): void => {
        if (!window.navigator.share) return;
        void window.navigator.share(args).catch(() => {
          /* user dismissed share sheet */
        });
      },
      [],
    );

    const handleFavoriteAdded = useCallback(() => {
      let removeSuccessMessage = (): void => {};

      const handleClick = (): void => {
        removeSuccessMessage();
        const event = createMxp365V2OpenEvent({
          module: 'mxp365-favorites',
          title: t('Merkliste'),
          token: currentToken,
          queryParams: currentQueryParams,
        });
        window.dispatchEvent(event);
      };

      removeSuccessMessage = push({
        title: t('Erfolgreich zur Merkliste hinzugefügt.'),
        text: (
          <Trans>
            Alle gemerkten Inhalte und Aktivitäten finden Sie auf{' '}
            <TextButton
              {...{ [MXP_DIALOG_NAV_ATTR]: true }}
              onClick={handleClick}
            >
              Ihrer Merkliste
            </TextButton>
          </Trans>
        ),
      });
    }, [push, t, currentToken, currentQueryParams]);

    const handleTitleChange = useCallback(
      ({ locationUri }: { locationUri: string }) => {
        const isLoginRedirect =
          locationUri.endsWith('/login') || locationUri.includes('/login?');
        if (isLoginRedirect) {
          setError(ErrorCode.Mxp365PersonFromTokenNotFound);
          return;
        }
        if (!titles) return;
        const extractedPath = extractPathFromLocationUri(locationUri);
        const title = findTitleByPathPrefix(extractedPath, titles);
        if (title) setDynamicTitle(title);
      },
      [titles],
    );

    const handleJwtLoginError = useCallback(
      ({ payload }: { payload?: Record<string, string | undefined> }) => {
        const isPersonNotFound = payload?.title
          ?.toLowerCase()
          .includes('person not found');
        const errorCode = isPersonNotFound
          ? ErrorCode.Mxp365PersonFromTokenNotFound
          : ErrorCode.Mxp365GeneralError;
        setError(errorCode);
      },
      [],
    );

    useEffect(() => {
      if (isOpen && currentModule && !currentToken) {
        setError(ErrorCode.Mxp365MissingToken);
      }
    }, [isOpen, currentModule, currentToken]);

    const handleClose = useCallback((open: boolean) => {
      setIsOpen(open);
      if (!open) {
        navGenerationRef.current++;
        setIsPending(true);
      }
    }, []);

    const shouldRenderIframe = hasBeenOpened && !!currentModule;

    return (
      <DialogPersistent
        open={isOpen}
        onOpenChange={handleClose}
        desktopSize="xl"
        disableSpacing
        onBack={showBackButton ? handleBack : undefined}
        showCloseButton
        expanded
        title={dynamicTitle ?? currentTitle}
        footerContent={
          notOnProd && (
            <Mxp365DebugFooter getIframeUrl={iframeRef.current?.getIframeUrl} />
          )
        }
      >
        <div className="relative bg-background-primary h-full text-marine-80 text-marine-high-emphasis">
          {shouldRenderIframe && currentModule && (
            <Mxp365IframeV2
              key={iframeKey}
              ref={iframeRef}
              token={currentToken}
              queryParams={currentQueryParams}
              initialModule={currentModule}
              initialParameter={currentParameter}
              className="bg-background-primary w-full h-full border-none relative"
              onMxpNavigate={handleNavigation}
              onMxpTitleChange={handleTitleChange}
              onMxpShare={handleShare}
              onMxpFavoriteAdded={handleFavoriteAdded}
              onMxpJwtLoginError={handleJwtLoginError}
              onReady={() => {
                setIsPending(false);
              }}
            />
          )}

          {(isPending || error) && (
            <div className="absolute inset-0 bg-[#f0f1f5] text-center text-marine-high-emphasis text-marine-80">
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                {isPending && !error && <AnimatedShipIndicator on="white" />}
                {error && <Mxp365Error error={error} />}
              </div>
            </div>
          )}
        </div>
      </DialogPersistent>
    );
  },
);
